/**
 * Per-page GTM auto-sync controls.
 *
 * Lives inside the Builder's tracking step, nested under the GTM
 * section. Surfaces:
 *
 *   1. "Not connected" CTA → link to /meta-capi to connect Google.
 *   2. Container picker — load operator's GTM accounts/containers,
 *      pre-select the one matching the GTM-ID input (if any), let
 *      them switch.
 *   3. Sync button — POST to /api/gtm/pages/:pageId/sync, show
 *      live progress + final summary (created/updated/skipped/deleted).
 *   4. Last sync indicator — "Last synced 12 min ago, 6 created,
 *      0 errors. Open workspace in GTM →" deep link.
 *
 * Failure modes the UI handles explicitly:
 *   • not_connected → "Connect Google first" CTA
 *   • token_revoked → "Token revoked, please reconnect" CTA
 *   • partial errors → render summary.errors[] inline as warnings
 *
 * Why this isn't a separate page:
 *   The operator is already in the Builder configuring their
 *   page's tracking — making them navigate to a separate GTM page
 *   to sync would be a context switch and a second-class workflow.
 *   Keeping it inline preserves "configure → sync → done" in one
 *   surface.
 */

import React from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import {
  Loader2,
  RefreshCw,
  CheckCircle2,
  AlertTriangle,
  ExternalLink,
  Plug,
  Save,
} from 'lucide-react';
import { useAuthStore } from '../../store/authStore';

const API_URL = 'https://api.hitzlink.com/api';

function getAuthToken(): string {
  const fromStore = useAuthStore.getState().token;
  if (fromStore) return fromStore;
  return localStorage.getItem('token') || '';
}

interface GtmAccount {
  accountId: string;
  name: string;
  containers: Array<{
    containerId: string;
    name: string;
    publicId: string;
    usageContext?: string[];
  }>;
  error?: string;
}

interface PageGtmLink {
  accountId: string;
  containerId: string;
  containerPublicId: string | null;
  workspaceId: string | null;
  workspaceName: string | null;
  lastSyncAt: string | null;
  lastSyncResult: {
    created: number;
    updated: number;
    skipped: number;
    deleted: number;
    errors: Array<{ kind: string; name: string; message: string }>;
  } | null;
}

interface Props {
  pageId: string;
  /** GTM Container ID typed in the parent input (GTM-XXXXXXX). Used to
   *  pre-select the matching container. If empty, picker still works
   *  but the operator has to scroll the list. */
  gtmIdHint: string;
  /** Persist the page's current Builder state before running the sync.
   *  CRITICAL — without this the operator can change Pixel IDs / GA4 ID
   *  / CAPI tokens in the Builder UI, click Sync, and have the sync run
   *  against STALE data from the DB (the React state never made it to
   *  Supabase). Should resolve to true on success, false on save failure
   *  (sync is then aborted). Optional so the panel still works in
   *  contexts that don't manage page state (e.g. settings dashboards). */
  onSaveBeforeSync?: () => Promise<boolean>;
}

export const GtmAutoSyncPanel: React.FC<Props> = ({
  pageId,
  gtmIdHint,
  onSaveBeforeSync,
}) => {
  const { t } = useTranslation('tracking');

  const [statusLoading, setStatusLoading] = React.useState(true);
  const [connected, setConnected] = React.useState(false);
  const [oauthEmail, setOauthEmail] = React.useState<string | null>(null);
  const [oauthConfigured, setOauthConfigured] = React.useState(true);

  const [accountsLoading, setAccountsLoading] = React.useState(false);
  const [accounts, setAccounts] = React.useState<GtmAccount[] | null>(null);

  const [link, setLink] = React.useState<PageGtmLink | null>(null);
  const [savingLink, setSavingLink] = React.useState(false);

  const [syncing, setSyncing] = React.useState(false);
  const [lastSyncToast, setLastSyncToast] = React.useState<string | null>(null);

  // ── Initial load: fetch connection status + saved link in parallel.
  React.useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const [statusRes, linkRes] = await Promise.all([
          fetch(`${API_URL}/gtm/status`, {
            headers: { Authorization: `Bearer ${getAuthToken()}` },
          }),
          fetch(`${API_URL}/gtm/pages/${pageId}/link`, {
            headers: { Authorization: `Bearer ${getAuthToken()}` },
          }),
        ]);
        const statusData = statusRes.ok ? await statusRes.json() : null;
        const linkData = linkRes.ok ? await linkRes.json() : null;
        if (cancelled) return;
        setStatusLoading(false);
        setOauthConfigured(statusData?.configured !== false);
        setConnected(Boolean(statusData?.oauth?.connected));
        setOauthEmail(statusData?.oauth?.email || null);
        setLink(linkData?.link || null);
      } catch {
        if (!cancelled) {
          setStatusLoading(false);
        }
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [pageId]);

  // ── Lazy-load accounts when connected (and only once). The list
  //    can be slow on accounts with many containers, so we don't
  //    block the initial render on it.
  const loadAccounts = React.useCallback(async () => {
    if (accounts !== null) return;
    setAccountsLoading(true);
    try {
      const r = await fetch(`${API_URL}/gtm/accounts`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      });
      const data = await r.json();
      if (!r.ok) {
        if (data?.code === 'not_connected') {
          setConnected(false);
        }
        throw new Error(data.error || 'Failed to load accounts');
      }
      setAccounts(data.accounts || []);
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setAccountsLoading(false);
    }
  }, [accounts]);

  React.useEffect(() => {
    if (connected && !accountsLoading && accounts === null) {
      loadAccounts();
    }
  }, [connected, accountsLoading, accounts, loadAccounts]);

  // saveLink is declared BEFORE the auto-pick effect that uses it so
  // the effect's closure resolves the const cleanly. useCallback keeps
  // the ref stable so we can safely include it in effect deps without
  // re-firing on every render.
  const saveLink = React.useCallback(
    async (next: { accountId: string; containerId: string; publicId: string }) => {
      setSavingLink(true);
      try {
        const r = await fetch(`${API_URL}/gtm/pages/${pageId}/link`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getAuthToken()}`,
          },
          body: JSON.stringify({
            accountId: next.accountId,
            containerId: next.containerId,
            containerPublicId: next.publicId,
          }),
        });
        const data = await r.json();
        if (!r.ok) throw new Error(data.error || 'Save failed');
        setLink(data.link);
        toast.success(t('gtmAutoSync.toastLinkSaved'));
      } catch (e) {
        toast.error((e as Error).message);
      } finally {
        setSavingLink(false);
      }
    },
    [pageId, t]
  );

  // ── Auto-pick + auto-save a container when the operator types a GTM
  //    ID upstream AND we've loaded accounts AND no link is saved yet.
  //
  // We BOTH set local state AND POST to the server. The first ship of
  // this component only set local state — operators would see the
  // matching container appear in the picker, assume it was saved,
  // click Sync, and hit a confusing "No GTM container linked" error
  // because the server had nothing persisted. Pre-select is a strong
  // signal of intent (the GTM ID matches a container the operator can
  // edit on their own Google account); auto-saving it removes the
  // surprise without losing the safety net — the operator can still
  // pick a different container from the dropdown later.
  React.useEffect(() => {
    if (link) return;
    if (!gtmIdHint) return;
    if (!accounts) return;
    const upper = gtmIdHint.toUpperCase().replace(/[^A-Z0-9-]/g, '');
    for (const acc of accounts) {
      const match = acc.containers.find((c) => c.publicId === upper);
      if (match) {
        saveLink({
          accountId: acc.accountId,
          containerId: match.containerId,
          publicId: match.publicId,
        });
        break;
      }
    }
  }, [accounts, gtmIdHint, link, saveLink]);

  const runSync = async () => {
    if (!link) return;
    setSyncing(true);
    setLastSyncToast(null);
    try {
      // Persist current Builder state FIRST so the server-side sync
      // sees the freshest Pixel IDs / GA4 ID / CAPI token selections.
      // Without this the operator can change a Pixel in the UI, click
      // Sync, and have the sync run against stale DB state — silent
      // failure mode that produces a confusing "missing Pixel tags"
      // result. The Save itself is silent (no toast) because we'll
      // emit a single "Synced — N created" toast afterwards anyway;
      // double-toasting on every Sync would be noisy.
      if (onSaveBeforeSync) {
        const saved = await onSaveBeforeSync();
        if (!saved) {
          toast.error(t('gtmAutoSync.toastSaveFailed'));
          return;
        }
      }

      const r = await fetch(`${API_URL}/gtm/pages/${pageId}/sync`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      });
      const data = await r.json();
      if (!r.ok) {
        if (data?.code === 'token_revoked') {
          setConnected(false);
          toast.error(t('gtmAutoSync.toastRevoked'));
          return;
        }
        throw new Error(data.error || 'Sync failed');
      }
      const s = data.summary || {};
      const summary = t('gtmAutoSync.toastSyncedSummary', {
        created: s.created || 0,
        updated: s.updated || 0,
        skipped: s.skipped || 0,
      });
      setLastSyncToast(summary);
      toast.success(summary);
      setLink((prev) => prev && {
        ...prev,
        workspaceId: data.workspaceId || prev.workspaceId,
        workspaceName: data.workspaceName || prev.workspaceName,
        lastSyncAt: new Date().toISOString(),
        lastSyncResult: s,
      });
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setSyncing(false);
    }
  };

  // ────────────────────────────────────────────────────────────────
  //  Render branches
  // ────────────────────────────────────────────────────────────────

  if (statusLoading) {
    return (
      <div className="flex items-center gap-2 text-xs text-gray-500 mt-3">
        <Loader2 className="w-3 h-3 animate-spin" />
        {t('gtmAutoSync.loading')}
      </div>
    );
  }

  // Server-side feature gate: nothing to show if operator hasn't set
  // up Google OAuth credentials. Stay silent rather than nagging —
  // they can always discover this on the Meta CAPI page.
  if (!oauthConfigured) {
    return null;
  }

  if (!connected) {
    return (
      <div className="mt-3 p-3 rounded-lg bg-blue-500/10 border border-blue-400/30">
        <div className="flex items-start gap-2">
          <Plug className="w-4 h-4 text-blue-300 mt-0.5 flex-shrink-0" />
          <div className="text-xs text-blue-100 leading-relaxed">
            <strong className="text-blue-50">{t('gtmAutoSync.notConnectedTitle')}</strong>
            <br />
            {t('gtmAutoSync.notConnectedBody')}{' '}
            <a
              href="/meta-capi"
              className="underline font-medium hover:text-white"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('gtmAutoSync.notConnectedLink')}
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-3 p-3 rounded-lg bg-gray-800/40 border border-gray-700/50 space-y-3">
      <div className="flex items-center gap-2 text-xs text-gray-300">
        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
        <span>
          {t('gtmAutoSync.connectedAs')}{' '}
          {oauthEmail && <span className="font-mono text-emerald-300">{oauthEmail}</span>}
        </span>
      </div>

      {/* Container picker */}
      <ContainerPicker
        accounts={accounts}
        loading={accountsLoading}
        currentLink={link}
        onSelect={(opt) => saveLink(opt)}
        onReload={() => {
          setAccounts(null);
          loadAccounts();
        }}
        saving={savingLink}
      />

      {/* Sync controls */}
      {link?.containerId && (
        <div className="flex flex-wrap items-center gap-2 pt-1">
          <button
            type="button"
            onClick={runSync}
            disabled={syncing}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 disabled:opacity-50 text-white text-xs font-medium shadow-sm"
          >
            {syncing ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : (
              <RefreshCw className="w-3 h-3" />
            )}
            {syncing ? t('gtmAutoSync.syncing') : t('gtmAutoSync.syncButton')}
          </button>
          {link.workspaceId && (
            <a
              href={`https://tagmanager.google.com/#/container/accounts/${link.accountId}/containers/${link.containerId}/workspaces/${link.workspaceId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs text-blue-300 hover:text-blue-200"
            >
              {t('gtmAutoSync.openWorkspace')}{' '}
              <ExternalLink className="w-3 h-3" />
            </a>
          )}
        </div>
      )}

      {/* Last sync result */}
      {(link?.lastSyncAt || lastSyncToast) && (
        <LastSyncSummary link={link} fallbackText={lastSyncToast} t={t} />
      )}
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────
//  Subcomponents
// ────────────────────────────────────────────────────────────────────

const ContainerPicker: React.FC<{
  accounts: GtmAccount[] | null;
  loading: boolean;
  currentLink: PageGtmLink | null;
  onSelect: (opt: { accountId: string; containerId: string; publicId: string }) => void;
  onReload: () => void;
  saving: boolean;
}> = ({ accounts, loading, currentLink, onSelect, onReload, saving }) => {
  const { t } = useTranslation('tracking');

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-xs text-gray-400">
        <Loader2 className="w-3 h-3 animate-spin" />
        {t('gtmAutoSync.loadingContainers')}
      </div>
    );
  }
  if (!accounts || accounts.length === 0) {
    return (
      <div className="flex items-center gap-2 text-xs text-amber-300">
        <AlertTriangle className="w-3 h-3" />
        {t('gtmAutoSync.noAccounts')}
        <button onClick={onReload} className="underline hover:text-amber-200">
          {t('gtmAutoSync.retry')}
        </button>
      </div>
    );
  }

  // Flatten to { value: "accountId|containerId", label: "Acct → Container (GTM-XXXXX)", ... }
  const options: Array<{
    value: string;
    label: string;
    accountId: string;
    containerId: string;
    publicId: string;
  }> = [];
  for (const acc of accounts) {
    for (const c of acc.containers) {
      options.push({
        value: `${acc.accountId}|${c.containerId}`,
        label: `${acc.name} → ${c.name} (${c.publicId})`,
        accountId: acc.accountId,
        containerId: c.containerId,
        publicId: c.publicId,
      });
    }
  }

  const currentValue = currentLink
    ? `${currentLink.accountId}|${currentLink.containerId}`
    : '';

  return (
    <div className="space-y-2">
      <label className="text-xs font-medium text-gray-300 flex items-center justify-between">
        {t('gtmAutoSync.pickerLabel')}
        <button
          type="button"
          onClick={onReload}
          className="text-[10px] text-gray-400 hover:text-gray-200 inline-flex items-center gap-1"
          title={t('gtmAutoSync.refreshList')}
        >
          <RefreshCw className="w-3 h-3" />
          {t('gtmAutoSync.refresh')}
        </button>
      </label>
      <select
        value={currentValue}
        onChange={(e) => {
          const opt = options.find((o) => o.value === e.target.value);
          if (opt) onSelect(opt);
        }}
        disabled={saving}
        className="w-full px-2 py-1.5 text-xs bg-gray-900/60 border border-gray-700 rounded-lg text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        <option value="" disabled>
          {t('gtmAutoSync.pickerPlaceholder')}
        </option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      {saving && (
        <div className="flex items-center gap-1.5 text-[10px] text-gray-400">
          <Save className="w-3 h-3" />
          {t('gtmAutoSync.saving')}
        </div>
      )}
    </div>
  );
};

const LastSyncSummary: React.FC<{
  link: PageGtmLink | null;
  fallbackText: string | null;
  t: (k: string, opts?: Record<string, unknown>) => string;
}> = ({ link, fallbackText, t }) => {
  if (!link?.lastSyncResult) {
    if (fallbackText) {
      return <div className="text-xs text-gray-400">{fallbackText}</div>;
    }
    return null;
  }
  const r = link.lastSyncResult;
  const when = link.lastSyncAt
    ? new Date(link.lastSyncAt).toLocaleString()
    : '';
  const totalErrors = (r.errors || []).length;
  return (
    <div className="text-[11px] text-gray-300 space-y-1">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-gray-400">{t('gtmAutoSync.lastSync', { when })}</span>
        <Badge tone="emerald" label={t('gtmAutoSync.created', { n: r.created || 0 })} />
        <Badge tone="blue" label={t('gtmAutoSync.updated', { n: r.updated || 0 })} />
        <Badge tone="gray" label={t('gtmAutoSync.skipped', { n: r.skipped || 0 })} />
        {(r.deleted || 0) > 0 && (
          <Badge tone="amber" label={t('gtmAutoSync.deleted', { n: r.deleted })} />
        )}
        {totalErrors > 0 && (
          <Badge tone="red" label={t('gtmAutoSync.errors', { n: totalErrors })} />
        )}
      </div>
      {totalErrors > 0 && (
        <details className="mt-1">
          <summary className="text-red-300 cursor-pointer hover:text-red-200">
            {t('gtmAutoSync.viewErrors')}
          </summary>
          <ul className="mt-1 ml-4 space-y-0.5 list-disc text-red-200">
            {r.errors.slice(0, 8).map((e, i) => (
              <li key={i}>
                <code className="text-red-100">{e.kind}/{e.name}</code>: {e.message}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
};

const TONES: Record<string, string> = {
  emerald: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  blue: 'bg-blue-500/15 text-blue-300 border-blue-500/30',
  gray: 'bg-gray-500/15 text-gray-300 border-gray-500/30',
  amber: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  red: 'bg-red-500/15 text-red-300 border-red-500/30',
};
const Badge: React.FC<{ tone: keyof typeof TONES; label: string }> = ({ tone, label }) => (
  <span
    className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border ${TONES[tone]}`}
  >
    {label}
  </span>
);
